import { AssessmentSessionDate, CalendarEventTypeId, ProcedureDto } from '../../../pages/Calendar/Calendar.types';
import { useStyles } from '../Calendar.styles';
import { EventBadge } from '../EventBadge/EventBadge.component';
import { useState } from 'react';
import { AddProcedure } from '../AddProcedure/AddProcedure.component';
import { Procedures } from '../Procedures/Procedures.component';
import { getEventName } from '../helpers';
import usePermission, { UserPermission } from '../../../hooks/usePermission';
import { Prompt } from '../../Prompt/Prompt.component';

export const CurrentMonth: React.FC<{ month: number; year: number; data: AssessmentSessionDate }> = ({
  month,
  year,
  data,
}) => {
  const classes = useStyles();

  const [isAddProcedureModalOpen, setIsAddProcedureModalOpen] = useState<boolean>(false);
  const [isProceduresModalOpen, setIsProceduresModalOpen] = useState<boolean>(false);
  const [selectedEvent, setSelectedEvent] = useState<CalendarEventTypeId>();
  const [isEditProcedure, setIsEditProcedure] = useState(false);
  const [selectedProcedure, setSelectedProcedure] = useState<ProcedureDto>();
  const [isErrorEventFinished, setIsErrorEventFinished] = useState<boolean>(false);

  const hasAdminCalendarManagePermission = usePermission(UserPermission.AdminCalendarManage);

  const DAYS_SHORT = ['Pon', 'Wt', 'Śr', 'Czw', 'Pt', 'So', 'Nd'];
  const ROWS_COUNT = 5;

  const selectedDate = new Date(year, month - 1);

  const DayLabels = () => {
    return (
      <>
        {DAYS_SHORT.map((dayLabel, index) => {
          return (
            <div key={index} className={classes.dayLabel}>
              {dayLabel}
            </div>
          );
        })}
      </>
    );
  };

  const toStartOfDay = (date: Date) => {
    const newDate = new Date(date);
    newDate.setHours(0);
    newDate.setMinutes(0);
    newDate.setSeconds(0);
    newDate.setMilliseconds(0);
    return newDate;
  };

  const currentDate = toStartOfDay(new Date());

  const startingDate = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1);
  startingDate.setDate(startingDate.getDate() - (startingDate.getDay() - 1));

  const dates: Date[] = [];
  for (let i = 0; i < ROWS_COUNT * 7; i++) {
    const date = new Date(startingDate);
    dates.push(date);
    startingDate.setDate(startingDate.getDate() + 1);
  }

  const getEndDateOfProcess = (eventType: CalendarEventTypeId) => {
    switch (eventType) {
      case CalendarEventTypeId.trainingProcess: {
        if (data.qualificationStart) {
          return data.qualificationStart;
        } else return data.scoringStart;
      }
      case CalendarEventTypeId.qualificationProcess: {
        return data.scoringStart;
      }
      case CalendarEventTypeId.scoringProcess: {
        return data.assessmentSessionEnd;
      }
      default:
        return data.assessmentSessionEnd;
    }
  };

  const handleAddProcedure = (eventType: CalendarEventTypeId) => {
    if (getEndDateOfProcess(eventType).getTime() <= new Date().getTime()) {
      setIsErrorEventFinished(true);
    } else {
      setSelectedEvent(eventType);
      setIsEditProcedure(false);
      setIsAddProcedureModalOpen(true);
    }
  };

  const handleEditProcedure = (procedure: ProcedureDto) => {
    setSelectedProcedure(procedure);
    setIsEditProcedure(true);
    setIsAddProcedureModalOpen(true);
  };

  const handleOpenProcedures = (eventType: CalendarEventTypeId) => {
    setSelectedEvent(eventType);
    setIsProceduresModalOpen(true);
  };

  return (
    <>
      <div className={classes.calendar}>
        <DayLabels />
        {dates.map((date, index) => {
          return (
            <div
              key={index}
              className={[
                classes.cell,
                date.getTime() == selectedDate.getTime() ? classes.currentMonth : '',
                date.getMonth() !== selectedDate.getMonth() ? classes.otherMonth : '',
              ].join(' ')}
            >
              <div
                className={[
                  classes.date,
                  date.getTime() === currentDate.getTime() ? classes.currentDate : '',
                  date.getMonth() !== selectedDate.getMonth() && date.getTime() !== currentDate.getTime()
                    ? classes.otherMonthDate
                    : '',
                ].join(' ')}
              >
                {date.getDate()}
              </div>
              {data.assessmentSessionStart && (
                <EventBadge
                  date={date}
                  index={index}
                  eventType={CalendarEventTypeId.assessmentSession}
                  assessmentSessionName={data.examTaskGroupNameAndSheetCode}
                  eventStartDate={data.assessmentSessionStart}
                  eventEndDate={data.assessmentSessionEnd}
                  onAddProcedure={handleAddProcedure}
                  onOpenProcedures={handleOpenProcedures}
                  isManagePermission={hasAdminCalendarManagePermission || data.isDomesticCoordinator}
                />
              )}
              {data.trainingStart && (
                <EventBadge
                  date={date}
                  index={index}
                  eventType={CalendarEventTypeId.trainingProcess}
                  eventStartDate={data.trainingStart}
                  eventEndDate={getEndDateOfProcess(CalendarEventTypeId.trainingProcess)}
                  isNextProces={!!data.qualificationStart}
                  onAddProcedure={handleAddProcedure}
                  onOpenProcedures={handleOpenProcedures}
                  isManagePermission={hasAdminCalendarManagePermission || data.isDomesticCoordinator}
                />
              )}
              {data.qualificationStart && (
                <EventBadge
                  date={date}
                  index={index}
                  eventType={CalendarEventTypeId.qualificationProcess}
                  eventStartDate={data.qualificationStart}
                  eventEndDate={getEndDateOfProcess(CalendarEventTypeId.qualificationProcess)}
                  isNextProces={!!data.scoringStart}
                  onAddProcedure={handleAddProcedure}
                  onOpenProcedures={handleOpenProcedures}
                  isManagePermission={hasAdminCalendarManagePermission || data.isDomesticCoordinator}
                />
              )}
              <EventBadge
                date={date}
                index={index}
                eventType={CalendarEventTypeId.scoringProcess}
                eventStartDate={data.scoringStart}
                eventEndDate={data.assessmentSessionEnd}
                onAddProcedure={handleAddProcedure}
                onOpenProcedures={handleOpenProcedures}
                isManagePermission={hasAdminCalendarManagePermission || data.isDomesticCoordinator}
              />
            </div>
          );
        })}
      </div>
      {isAddProcedureModalOpen && selectedEvent !== undefined && (
        <AddProcedure
          data={selectedProcedure}
          isEdit={isEditProcedure}
          calendarEventTypeId={selectedEvent}
          assessmentSessionId={data.assementSessionId}
          onClose={() => {
            setIsProceduresModalOpen(false),
              setIsAddProcedureModalOpen(false),
              setIsEditProcedure(false),
              setSelectedProcedure(undefined);
          }}
        />
      )}
      {isProceduresModalOpen && selectedEvent !== undefined && (
        <Procedures
          handleOpenEditProcedureModal={handleEditProcedure}
          eventName={getEventName(selectedEvent, data.examTaskGroupNameAndSheetCode)}
          calendarEventTypeId={selectedEvent}
          assessmentSessionId={data.assementSessionId}
          handleClose={() => {
            setIsProceduresModalOpen(false), setSelectedProcedure(undefined);
          }}
        />
      )}
      {isErrorEventFinished && (
        <Prompt
          type="warning"
          open={true}
          mainText={'Nie można dodać nowej procedury do zakończonego zdarzenia.'}
          isBold={true}
          mainActionButtonText={'OK'}
          onConfirm={() => {
            setIsErrorEventFinished(false);
          }}
        />
      )}
    </>
  );
};
